import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import blogsIcon from '../../assests/blogs.png';

interface BlogPost {
  id: string;
  title: string;
  published: string;
  url: string;
  summary: string;
  labels: string[];
}

// Blogger RSS feed URL
const BLOGGER_FEED_URL = 'https://whynottoufiqwrites.blogspot.com';

function formatDate(dateStr: string) {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function BlogCard({ post, index }: { post: BlogPost; index: number }) {
  return (
    <motion.a
      href={post.url}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="card-hover block p-6 rounded-xl bg-white/[0.03] border border-white/[0.07] group"
    >
      {/* Labels */}
      {post.labels && post.labels.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {post.labels.slice(0, 3).map((label) => (
            <span
              key={label}
              className="px-2 py-0.5 rounded-full text-[10px] bg-[#00B92E]/10 text-[#00B92E] border border-[#00B92E]/20"
            >
              {label}
            </span>
          ))}
        </div>
      )}

      <h3 className="text-white font-semibold text-sm leading-snug mb-2 group-hover:text-[#00B92E] transition-colors line-clamp-2">
        {post.title}
      </h3>

      {post.summary && (
        <p className="text-white/50 text-xs leading-relaxed mb-4 line-clamp-3">
          {post.summary}
        </p>
      )}

      <div className="flex items-center justify-between">
        <span className="text-white/30 text-xs">{formatDate(post.published)}</span>
        <svg
          className="w-4 h-4 text-white/30 group-hover:text-[#00B92E] group-hover:translate-x-1 transition-all"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
        </svg>
      </div>
    </motion.a>
  );
}

export default function Blogs() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [noFeed] = useState(!BLOGGER_FEED_URL);

  useEffect(() => {
    if (!BLOGGER_FEED_URL) {
      setLoading(false);
      return;
    }

    // Use rss2json proxy to bypass CORS issues with direct Blogger JSON feeds
    const rssUrl = `${BLOGGER_FEED_URL.replace(/\/$/, '')}/feeds/posts/default?alt=rss`;
    const feedUrl = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(rssUrl)}`;

    fetch(feedUrl)
      .then((res) => res.json())
      .then((data) => {
        if (data.status !== 'ok') throw new Error('Failed to fetch');
        const items = data.items || [];
        const parsed: BlogPost[] = items.map((item: any) => {
          let finalTitle = item.title?.trim();
          if (!finalTitle) {
            const h1Match = item.description?.match(/<h1[^>]*>([\s\S]*?)<\/h1>/i);
            if (h1Match && h1Match[1]) {
              finalTitle = h1Match[1].replace(/<[^>]*>?/gm, '').trim();
            } else {
              const titleMatch = item.description?.match(/<title[^>]*>([\s\S]*?)<\/title>/i);
              if (titleMatch && titleMatch[1]) {
                finalTitle = titleMatch[1].replace(/<[^>]*>?/gm, '').trim();
              }
            }
          }
          finalTitle = finalTitle || 'Untitled';

          // Create a summary by stripping HTML, removing the title if it appears at the start, and truncating
          let plainText = item.description?.replace(/<[^>]*>?/gm, '').trim() || '';
          if (finalTitle && plainText.startsWith(finalTitle)) {
            plainText = plainText.substring(finalTitle.length).trim();
          }

          return {
            id: item.guid || Math.random().toString(),
            title: finalTitle,
            published: item.pubDate || '',
            url: item.link || '#',
            summary: plainText.substring(0, 150) + (plainText.length > 150 ? '...' : ''),
            labels: (item.categories || []).slice(0, 3),
          };
        });
        setPosts(parsed);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  return (
    <section id="blogs" className="py-24 md:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-3 mb-10"
        >
          <img src={blogsIcon} alt="" className="w-8 h-8 object-contain" />
          <h2
            className="text-white font-bold section-label"
            style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', letterSpacing: '0.05em' }}
          >
            Blogs
          </h2>
        </motion.div>

        {/* No feed configured */}
        {noFeed && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col items-start gap-4"
          >
            <div className="p-8 rounded-xl bg-white/[0.02] border border-white/[0.06] w-full max-w-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-2 h-2 rounded-full bg-[#00B92E] animate-pulse" />
                <span className="text-white/40 text-xs tracking-widest uppercase">
                  Retrieving blogs from Blogger account
                </span>
              </div>
              <p className="text-white/60 text-sm leading-relaxed">
                Retrieving blogs from my blogger account and presenting them here.
              </p>
              <p className="text-white/30 text-xs mt-3">
                ⚙️ Add your Blogger URL in{' '}
                <code className="text-[#00B92E]/70 bg-[#00B92E]/5 px-1 py-0.5 rounded">
                  src/components/Blogs.tsx
                </code>{' '}
                to enable live blog fetching.
              </p>
            </div>
          </motion.div>
        )}

        {/* Loading */}
        {!noFeed && loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="h-44 rounded-xl bg-white/[0.03] border border-white/[0.05] animate-pulse"
              />
            ))}
          </div>
        )}

        {/* Posts */}
        {!noFeed && !loading && posts.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {posts.map((post, i) => (
              <div key={post.id} className={i >= 2 ? 'hidden lg:block' : 'block'}>
                <BlogCard post={post} index={i} />
              </div>
            ))}
          </div>
        )}

        {/* No posts after fetch */}
        {!noFeed && !loading && posts.length === 0 && (
          <p className="text-white/40 text-sm">No blog posts found.</p>
        )}
      </div>
    </section>
  );
}
